import PropTypes from "prop-types";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Slide from "@mui/material/Slide";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

const colorMap = {
  success: { icon: "check_circle", hex: "#22c55e" },
  error: { icon: "error", hex: "#ef4444" },
  warning: { icon: "warning", hex: "#f59e0b" },
  info: { icon: "info", hex: "#3b82f6" },
  primary: { icon: "notifications", hex: "#7c3aed" },
  secondary: { icon: "notifications", hex: "#6b7280" },
  dark: { icon: "notifications", hex: "#111827" },
  light: { icon: "notifications", hex: "#9ca3af" },
};

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

function MDSnackbar({ color, icon, title, content, close, ...rest }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const resolved = colorMap[color] || colorMap.info;
  const accentColor = resolved.hex;
  const resolvedIcon = icon || resolved.icon;

  const bg = darkMode ? "#1e2736" : "#ffffff";
  const titleCol = darkMode ? "#f9fafb" : "#111827";
  const contentCol = darkMode ? "#9ca3af" : "#6b7280";
  const borderCol = darkMode ? "#374151" : "#e5e7eb";

  return (
    <Snackbar
      TransitionComponent={SlideTransition}
      autoHideDuration={5000}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      {...rest}
    >
      <MDBox
        display="flex"
        alignItems="stretch"
        sx={{
          background: bg,
          border: `1px solid ${borderCol}`,
          borderLeft: `4px solid ${accentColor}`,
          borderRadius: "8px",
          boxShadow: darkMode
            ? "0 4px 24px rgba(0,0,0,0.5)"
            : "0 4px 24px rgba(0,0,0,0.10)",
          minWidth: "300px",
          maxWidth: "420px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Icon column */}
        <MDBox
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          sx={{ pt: 1.75, pl: 1.5, pr: 0.5 }}
        >
          <Icon sx={{ color: accentColor, fontSize: "20px !important", mt: "1px" }}>
            {resolvedIcon}
          </Icon>
        </MDBox>

        {/* Text column */}
        <MDBox sx={{ py: 1.5, pr: 1, pl: 0.5, flex: 1, minWidth: 0 }}>
          {title ? (
            <MDTypography
              variant="button"
              fontWeight="medium"
              sx={{ color: titleCol, display: "block", lineHeight: 1.4, fontSize: "13px" }}
            >
              {title}
            </MDTypography>
          ) : null}
          {content ? (
            <MDTypography
              variant="caption"
              sx={{
                color: contentCol,
                display: "block",
                lineHeight: 1.5,
                fontSize: "12px",
                mt: title ? 0.25 : 0,
              }}
            >
              {content}
            </MDTypography>
          ) : null}
        </MDBox>

        {/* Close button */}
        <MDBox display="flex" alignItems="flex-start" sx={{ pt: 0.75, pr: 0.75 }}>
          <IconButton
            size="small"
            onClick={close}
            sx={{
              color: darkMode ? "#6b7280" : "#9ca3af",
              padding: "4px",
              "&:hover": { color: darkMode ? "#f9fafb" : "#374151", background: "transparent" },
            }}
          >
            <Icon sx={{ fontSize: "16px !important" }}>close</Icon>
          </IconButton>
        </MDBox>
      </MDBox>
    </Snackbar>
  );
}

MDSnackbar.defaultProps = {
  bgWhite: false,
  color: "info",
  icon: null,
  dateTime: "",
  content: "",
};

MDSnackbar.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string,
  content: PropTypes.node,
  close: PropTypes.func.isRequired,
  bgWhite: PropTypes.bool,
};

export default MDSnackbar;
